import React, { useState } from "react";
import {
  TextField,
  Button,
  Box,
  Typography,
  Card,
  IconButton,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { AiFillCheckCircle } from "react-icons/ai";
import { useTranslation } from "react-i18next";
import "./ChangePasswordCard.css";

const ChangePasswordCard = ({ onClose }) => {
  const { t, i18n } = useTranslation(["ui"]);
  const isArabic = i18n.language === "ar";
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState(null);
  const [strengthVisible, setStrengthVisible] = useState(false);
  const [strength, setStrength] = useState({
    minLength: false,
    hasUpperCase: false,
    hasLowerCase: false,
    hasNumber: false,
    hasSymbol: false,
  });

  const toggleShowNewPassword = () => {
    setShowNewPassword(!showNewPassword);
  };

  const toggleShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const checkPasswordStrength = (password) => {
    setStrengthVisible(password.length > 0);
    setStrength({
      minLength: password.length >= 8,
      hasUpperCase: /[A-Z]/.test(password),
      hasLowerCase: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSymbol: /[^A-Za-z0-9]/.test(password),
    });
  };

  const handleNewPasswordChange = (e) => {
    const password = e.target.value;
    setNewPassword(password);
    checkPasswordStrength(password);
  };

  const handleChangePassword = () => {
    if (!currentPassword.trim() || !newPassword.trim()) {
      setError(t("signup.validation.allRequired", { defaultValue: "Please fill in all fields." }));
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setError(t("signup.validation.passwordMismatch"));
      return;
    }

    const allStrengthPassed = Object.values(strength).every(Boolean);
    if (!allStrengthPassed) {
      setError(
        t("signup.validation.passwordStrength", {
          defaultValue: "Please meet all password requirements.",
        })
      );
      return;
    }

    // Dispatch an action to change the password
    // Example: dispatch(changePassword(currentPassword, newPassword));
    setError(null);
    onClose(); // Close the card after password change
  };

  return (
    <div className="change-password-backdrop">
      <Card className="change-password-card">
        <Typography variant="h5" gutterBottom>
          {t("signup.changePasswordTitle", { defaultValue: "Change Password" })}
        </Typography>
        <Box sx={{ mb: 2 }}>
          <TextField
            label={t("signup.currentPassword", { defaultValue: "Current Password" })}
            type="password"
            variant="outlined"
            fullWidth
            margin="normal"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
          />
          <TextField
            label={t("signup.password")}
            type={showNewPassword ? "text" : "password"}
            variant="outlined"
            fullWidth
            margin="normal"
            value={newPassword}
            onChange={handleNewPasswordChange}
            InputProps={{
              endAdornment: (
                <IconButton onClick={toggleShowNewPassword}>
                  {showNewPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              ),
            }}
          />
          <TextField
            label={t("signup.confirmPassword")}
            type={showConfirmPassword ? "text" : "password"}
            variant="outlined"
            fullWidth
            margin="normal"
            value={confirmNewPassword}
            onChange={(e) => setConfirmNewPassword(e.target.value)}
            InputProps={{
              endAdornment: (
                <IconButton onClick={toggleShowConfirmPassword}>
                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              ),
            }}
          />
        </Box>
        {strengthVisible && (
          <Box
            className="strength-indicator"
            sx={{ display: "flex", flexDirection: "column", mb: 2 }}
          >
            <Typography color={strength.minLength ? "green" : "gray"}>
              <AiFillCheckCircle /> {t("signup.passwordStrength.minLength")}
            </Typography>
            <Typography color={strength.hasUpperCase ? "green" : "gray"}>
              <AiFillCheckCircle /> {t("signup.passwordStrength.uppercase")}
            </Typography>
            <Typography color={strength.hasLowerCase ? "green" : "gray"}>
              <AiFillCheckCircle /> {t("signup.passwordStrength.lowercase")}
            </Typography>
            <Typography color={strength.hasNumber ? "green" : "gray"}>
              <AiFillCheckCircle /> {t("signup.passwordStrength.number")}
            </Typography>
            <Typography color={strength.hasSymbol ? "green" : "gray"}>
              <AiFillCheckCircle />{" "}
              {t("signup.passwordStrength.symbol", {
                defaultValue: isArabic
                  ? "رمز واحد على الأقل (مثل !@#$%)"
                  : "At least one symbol (e.g., !@#$%)",
              })}
            </Typography>
          </Box>
        )}
        {error && (
          <Typography color="error" sx={{ mb: 1 }}>
            {error}
          </Typography>
        )}
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleChangePassword}
          >
            {t("signup.update", { defaultValue: "Update" })}
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={onClose}
          >
            {t("signup.back", { defaultValue: "Back" })}
          </Button>
        </Box>
      </Card>
    </div>
  );
};

export default ChangePasswordCard;
